import { t } from 'i18next';

import { ContactGroupIdCell, ContactGroupNameCell } from 'contacts/components/ContactGroupList';
import { ContactGroupType } from 'contacts/enums';
import { IContactGroupAddress, IEmployee } from 'contacts/types';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { ChipListCell, SwitchCell } from 'core/elements';

import { ActionsCell, EmployeeCell } from './Cells';

export const locationColumnDef = ({
  contactGroupType,
  isGlobal = true,
  openContactGroupDetails = () => {
    /* no-op */
  },
}: {
  contactGroupType: ContactGroupType;
  isGlobal?: boolean;
  openContactGroupDetails?: (data: IContactGroupAddress) => void;
}) => {
  const {
    companyIdColumn,
    companyColumn,
    nameColumn,
    addressColumn,
    zipColumn,
    cityColumn,
    stateColumn,
    countryColumn,
    isrsColumn,
    osrsColumn,
    actionsColumn,
  } = commonColumns(openContactGroupDetails, contactGroupType, isGlobal);

  const {
    accountsReceivableNumberColumn,
    suggestedAccountsReceivableNumberColumn,
    customerNumberColumn,
    shipToColumn,
    billToColumn,
  } = customerColumns();

  const {
    accountsPayableNumberColumn,
    supplierNumberColumn,
    vendorNumbersColumn,
    legacyVendorNumbersColumn,
    spaEnabledColumn,
    minimumOrderFeeColumn,
    dropShipFreightTermsColumn,
  } = vendorColumns();

  switch (contactGroupType) {
    case ContactGroupType.CUSTOMER:
      return [
        actionsColumn,
        ...(isGlobal ? [companyIdColumn, companyColumn] : []),
        accountsReceivableNumberColumn,
        suggestedAccountsReceivableNumberColumn,
        customerNumberColumn,
        nameColumn,
        addressColumn,
        zipColumn,
        cityColumn,
        stateColumn,
        countryColumn,
        shipToColumn,
        billToColumn,
        isrsColumn,
        osrsColumn,
      ];
    case ContactGroupType.VENDOR:
      return [
        actionsColumn,
        ...(isGlobal ? [companyIdColumn, companyColumn] : []),
        accountsPayableNumberColumn,
        supplierNumberColumn,
        vendorNumbersColumn,
        legacyVendorNumbersColumn,
        nameColumn,
        addressColumn,
        zipColumn,
        cityColumn,
        stateColumn,
        countryColumn,
        spaEnabledColumn,
        minimumOrderFeeColumn,
        dropShipFreightTermsColumn,
      ];
    default:
      return [];
  }
};

export const commonColumns = (
  openContactGroupDetails: (data: IContactGroupAddress) => void,
  contactGroupType: ContactGroupType,
  isGlobal?: boolean
) => ({
  actionsColumn: {
    colId: 'actions',
    headerName: '',
    field: 'actions',
    width: 50,
    maxWidth: 50,
    resizable: false,
    sortable: false,
    filter: false,
    enableRowGroup: false,
    suppressHeaderMenuButton: true,
    suppressColumnsToolPanel: true,
    pinned: 'left',
    lockPinned: true,
    suppressMovable: true,
    cellRenderer: ActionsCell,
    cellRendererParams: {
      contactGroupType,
      isGlobal,
    },
  },
  companyIdColumn: {
    colId: 'contactGroupId',
    headerName: t('headerCompanyId'),
    field: 'contactGroup.id',
    filter: 'agNumberColumnFilter',
    width: 100,
    cellRenderer: ContactGroupIdCell,
    cellRendererParams: {
      colId: 'contactGroup.id',
      openDetails: openContactGroupDetails,
    },
  },
  companyColumn: {
    colId: 'contactGroupName',
    headerName: t('headerCompany'),
    field: 'contactGroup.name',
    filter: 'agTextColumnFilter',
    minWidth: 160,
    width: 280,
    cellRenderer: ContactGroupNameCell,
    cellRendererParams: {
      colId: 'contactGroup.name',
      openDetails: openContactGroupDetails,
    },
  },
  nameColumn: {
    colId: 'name',
    headerName: t('headerLocationName'),
    field: 'name',
    filter: 'agTextColumnFilter',
  },
  addressColumn: {
    colId: 'addressLines',
    headerName: t('headerAddress'),
    field: 'addressLines',
    filter: 'agTextColumnFilter',
    valueGetter: ({ data }) => {
      if (!data) {
        return DEFAULT_PLACEHOLDER;
      }

      const { line1, line2 } = data;

      return [line1, line2].filter(Boolean).join(' ');
    },
  },
  zipColumn: {
    colId: 'zip',
    headerName: t('headerZip'),
    field: 'zip',
    filter: 'agTextColumnFilter',
  },
  cityColumn: {
    colId: 'city',
    headerName: t('headerCity'),
    field: 'city',
    filter: 'agTextColumnFilter',
  },
  stateColumn: {
    colId: 'stateName',
    headerName: t('headerState'),
    field: 'stateName',
    filter: 'agTextColumnFilter',
  },
  countryColumn: {
    colId: 'countryName',
    headerName: t('headerCountry'),
    field: 'countryName',
    filter: 'agTextColumnFilter',
  },
  isrsColumn: {
    colId: 'isrs',
    headerName: t('headerIsrEmployees'),
    field: 'isrs',
    filter: 'agTextColumnFilter',
    minWidth: 300,
    width: 300,
    maxWidth: 450,
    enableRowGroup: false,
    sortable: false,
    filterValueGetter: ({ data }) => {
      if (!data || !data?.isrs?.length) {
        return undefined;
      }

      return data.isrs.map((employee: IEmployee) =>
        [employee.employeeName, employee.employeeId, employee.email, employee.employeeId]
          .filter(Boolean)
          .join(' ')
      );
    },
    cellRenderer: EmployeeCell,
  },
  osrsColumn: {
    colId: 'osrs',
    headerName: t('headerOsrEmployees'),
    field: 'osrs',
    filter: 'agTextColumnFilter',
    minWidth: 300,
    width: 300,
    maxWidth: 450,
    enableRowGroup: false,
    sortable: false,
    filterValueGetter: ({ data }) => {
      if (!data || !data?.osrs?.length) {
        return undefined;
      }

      return data.osrs
        .map((employee: IEmployee) =>
          [employee.employeeName, employee.employeeId, employee.email, employee.employeeId]
            .filter(Boolean)
            .join(' ')
        )
        .filter(Boolean)
        .join(' ');
    },
    cellRenderer: EmployeeCell,
  },
});

export const customerColumns = () => ({
  accountsReceivableNumberColumn: {
    colId: 'accountsReceivableId',
    headerName: t('headerARNumber'),
    field: 'accountsReceivableId',
    filter: 'agTextColumnFilter',
  },
  suggestedAccountsReceivableNumberColumn: {
    colId: 'suggestedAccountsReceivableId',
    headerName: t('headerSuggestedARNumber'),
    field: 'suggestedAccountsReceivableId',
    filter: 'agTextColumnFilter',
    hide: true,
  },
  customerNumberColumn: {
    colId: 'externalCustomerNumber',
    headerName: t('headerCustomerNumber'),
    field: 'externalCustomerNumber',
    filter: 'agTextColumnFilter',
  },
  shipToColumn: {
    colId: 'isShipTo',
    headerName: t('headerShipTo'),
    field: 'isShipTo',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [0, 1],
      valueFormatter: ({ value }) => (value === 1 ? t('labelEnabled') : t('labelDisabled')),
    },
    cellDataType: 'boolean',
    cellRenderer: SwitchCell,
    cellRendererParams: {
      colId: 'isShipTo',
      isEditable: (data: IContactGroupAddress) => !data?.accountsReceivableId,
    },
  },
  billToColumn: {
    colId: 'isBillTo',
    headerName: t('headerBillTo'),
    field: 'isBillTo',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [0, 1],
      valueFormatter: ({ value }) => (value === 1 ? t('labelEnabled') : t('labelDisabled')),
    },
    cellRenderer: SwitchCell,
    cellDataType: 'boolean',
    cellRendererParams: {
      colId: 'isBillTo',
      isEditable: () => false,
    },
  },
});

export const vendorColumns = () => ({
  accountsPayableNumberColumn: {
    colId: 'accountsPayableId',
    headerName: t('headerAPNumber'),
    field: 'accountsPayableId',
    filter: 'agTextColumnFilter',
  },
  supplierNumberColumn: {
    colId: 'contactGroupAlternateVendorId',
    headerName: t('headerSupplierNumber'),
    field: 'contactGroup.alternateVendorId',
    filter: 'agTextColumnFilter',
  },
  vendorNumbersColumn: {
    colId: 'contactGroupLegacySupplierNumbers',
    headerName: t('headerVendorNumbers'),
    field: 'contactGroup.legacySupplierNumbers',
    filter: 'agTextColumnFilter',
    cellRenderer: ChipListCell,
  },
  legacyVendorNumbersColumn: {
    colId: 'contactGroupLegacyVendorNumbers',
    headerName: t('headerLegacyVendorNumbers'),
    field: 'contactGroup.legacyVendorNumbers',
    filter: 'agTextColumnFilter',
    cellRenderer: ChipListCell,
  },
  spaEnabledColumn: {
    colId: 'spaEnabled',
    headerName: t('headerSpaEnabled'),
    field: 'spaEnabled',
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [false, true],
      valueFormatter: ({ value }) => (value ? t('labelEnabled') : t('labelDisabled')),
    },
    cellRenderer: SwitchCell,
    cellDataType: 'boolean',
    cellRendererParams: {
      colId: 'spaEnabled',
      isEditable: () => false,
    },
  },
  minimumOrderFeeColumn: {
    colId: 'minimumOrderFee',
    headerName: t('headerMinimumOrderFee'),
    field: 'minimumOrderFee',
    sortable: false,
  },
  dropShipFreightTermsColumn: {
    colId: 'dsFreightTerms',
    headerName: t('headerDropShipFreightTerms'),
    field: 'dsFreightTerms',
    sortable: false,
  },
});
